import { LoaderCircle, Plus } from 'lucide-react'
import { useCallback, useContext, useState } from 'react'

import EmployerContext from 'components/context/EmployerContext'
import { CollapsibleDataContainer } from 'components/reusable/CollapsibleDataContainer'
import { Banner, TextLink, Tooltip } from 'slab'
import { useGetAdminEmployersEmployerIdComplianceAudits } from 'utils/web_api'
import { type AdminComplianceAudit, PostAdminEmployersEmployerIdComplianceAuditsAuditType } from 'utils/web_api.schemas'

import AuditCard from './AuditCard'
import EmployerComplianceBankPartnerSheet from './EmployerComplianceBankPartnerSheet'

const AVAILABLE_PARTNERS = Object.values(PostAdminEmployersEmployerIdComplianceAuditsAuditType)

const Heading = ({ onAddPartnerClick, hasAvailablePartners }: { onAddPartnerClick: () => void; hasAvailablePartners: boolean }) => (
  <div className='flex items-center justify-between'>
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>Bank Partners</span>
      {hasAvailablePartners
        ? (
          <div className='cursor-pointer' onClick={(e) => e.stopPropagation()}>
            <TextLink color='purple' leftIcon={Plus} onClick={onAddPartnerClick}>
              Add Bank Partner
            </TextLink>
          </div>
          )
        : (
          <Tooltip content='All available bank partners have been added'>
            <div className='cursor-not-allowed opacity-50'>
              <TextLink color='gray' leftIcon={Plus} disabled>
                Add Bank Partner
              </TextLink>
            </div>
          </Tooltip>
          )}
    </div>
  </div>
)

const BankPartnersView = () => {
  const { employer } = useContext(EmployerContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { data, isLoading, error } = useGetAdminEmployersEmployerIdComplianceAudits<AdminComplianceAudit[]>(
    employer!.id,
    {
      query: {
        enabled: true,
      },
    }
  )
  const audits = data ?? []
  const existingPartners = audits.map(a => a.partner_name)
  const availablePartners = AVAILABLE_PARTNERS.filter((partner: PostAdminEmployersEmployerIdComplianceAuditsAuditType) => !existingPartners.includes(partner))
  const hasAvailablePartners = availablePartners.length > 0

  const unitAudit = audits.find((audit) => audit.partner_name === 'Unit')

  const handleAddPartnerClick = useCallback(() => {
    if (hasAvailablePartners) {
      setIsSheetOpen(true)
    }
  }, [hasAvailablePartners])

  const successContent = (
    <div className='p-2'>
      <div className='space-y-4'>
        {/* NOTE(grant): This message will show until we figure out how to display ERs that have been added to Unit. */}
        {employer?.review_status === 'approved' && !unitAudit && (
          <div className='mb-4 p-3 bg-gray-50 rounded-md border border-gray-200'>
            <span className='font-bold text-md'>⚠️ Note: This employer may be onboarded to Unit, even if it doesn't show up here.</span>
          </div>
        )}

        {audits.length > 0 && (
          <div className='grid gap-4 md:grid-cols-2'>
            {audits.map((audit) => (
              <AuditCard key={audit.id} audit={audit} />
            ))}
          </div>
        )}

        {audits.length === 0 && (
          <div className='text-center py-8'>
            <p className='text-sm text-gray-500'>No bank partners found.</p>
          </div>
        )}
      </div>
      <EmployerComplianceBankPartnerSheet
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        employerId={employer!.id}
        availablePartnerOptions={availablePartners}
      />
    </div>
  )

  const errorContent = (
    <Banner title='Error loading bank partner details. Please try again later.' />
  )

  const loadingContent = (
    <div className='text-center py-4'>
      <div className='text-gray-500 animate-spin mx-auto w-fit'>
        <LoaderCircle size={24} />
      </div>
      <p className='mt-2 text-sm text-gray-600'>Fetching bank partners...</p>
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading={
        <Heading onAddPartnerClick={handleAddPartnerClick} hasAvailablePartners={hasAvailablePartners} />
      }
      isLoading={isLoading}
      error={!!error}
      localStorageKey='bank-partners-view'
      loadingContent={loadingContent}
      errorContent={errorContent}
      successContent={successContent}
    />
  )
}

export default BankPartnersView
